import { Stack } from 'component-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts-overriden';

import { getXAxisDomainForHistoricGraph, minMaxAxisDomain } from '@/components/Charts/utils';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePathSet } from '@/hooks/usePathSet';
import { useProjectDetailById } from '@/pages/shared/hooks/useProjectDetailById';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import {
  TileCapsuleContent,
  TileCapsuleTitle,
  TileCta,
} from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/land-monitoring/components/Tile';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

type TileProps = {
  chart: ReactNode;
  capsuleTitle: ReactNode;
  capsuleContent: ReactNode;
  cta: ReactNode;
  helper: ReactNode;
};

const Tile = ({ chart, capsuleTitle, capsuleContent, cta, helper }: TileProps) => {
  return (
    <Stack spacing={8} className='h-[300px] justify-between'>
      <Stack spacing={4} className='mt-[30px]'>
        {chart}
        <Stack spacing={2}>
          {capsuleTitle}
          {capsuleContent}
        </Stack>
      </Stack>
      <Stack spacing={4} className='items-start'>
        {cta}
        {helper}
      </Stack>
    </Stack>
  );
};

export const WaterTile = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const waterHoldingCapacityPerHa = useFact('r1_water_holding_capacity_per_ha')?.value;

  const project = useProjectDetailById().data;
  const historicWhcPerHaGraph = Array.isArray(waterHoldingCapacityPerHa)
    ? waterHoldingCapacityPerHa
        .filter((d) => d.name === 'water_holding_capacity_per_ha')
        // @ts-ignore typescript does not allow arithmetic operations between date objects
        .sort((pointA, pointB) => new Date(pointA.date) - new Date(pointB.date))
        .map((d) => ({ ...d, date: new Date(d.date).getTime() }))
    : [];

  const whcLatestAnalysis = useFact('r1_water_holding_capacity_latest_analysis_per_ha')?.value as number | null;

  const chartData = historicWhcPerHaGraph;

  let whcLatestAnalysisDisplay = `${useDisplayNumber(whcLatestAnalysis ?? 0)} ${t('global.ui.unit.m^3/ha')}`;
  whcLatestAnalysisDisplay = !isNullish(whcLatestAnalysis) ? whcLatestAnalysisDisplay : '-';

  const noChartData = !chartData?.length;

  if (noChartData) {
    return null;
  }

  return (
    <Stack spacing={8} className='flex-1 rounded-[32px] bg-white-100 p-8'>
      <span className='typography-h3'>{t('global.analysis.waterHoldingCapacity')}</span>
      <Stack spacing={6} className='rounded-2xl bg-bg-light-grey p-6'>
        <Tile
          chart={<Chart chartData={chartData} />}
          capsuleTitle={<TileCapsuleTitle>{t('global.analysis.waterHoldingCapacity')}</TileCapsuleTitle>}
          capsuleContent={<TileCapsuleContent>{whcLatestAnalysisDisplay}</TileCapsuleContent>}
          cta={
            <TileCta to={buildPath(pathSet.projectDetailsWater, { pathParams: { projectId: project.id } })}>
              {t('shared.ncaDetail.details.ecologicalIntegrity.labels.seeMoreDetails')}
            </TileCta>
          }
          helper={null}
        />
      </Stack>
    </Stack>
  );
};

type HistoricWaterAndSoilMoistureGraphData = {
  date: number;
  name: 'water_holding_capacity_per_ha';
  value: number;
  unit: string;
};
const Chart = ({ chartData }: { chartData: HistoricWaterAndSoilMoistureGraphData[] }) => {
  const firstTick = chartData.at(0)?.date;
  const lastTick = chartData.at(-1)?.date;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick!, lastTick!) ?? [
    new Date().getFullYear(),
    new Date().getFullYear(),
  ];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startYear = xTicksDomain[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const endYear = xTicksDomain[1]!;
  const ticksRange = Array(endYear - startYear + 1).keys();
  const xTicks = Array.from(ticksRange).map((x) => new Date(`${startYear + x}-12-31`).getTime());

  return (
    <ResponsiveContainer data-testid='water-historic-chart-tile' width='100%' height={80}>
      <AreaChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
      >
        <defs>
          <linearGradient id='waterHoldingCapacity' x1='0' y1='-0.85' x2='0' y2='1'>
            <stop offset='0%' stopColor='#6ACDE1' stopOpacity={1} />
            <stop offset='100%' stopColor='#6ACDE1' stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          isAnimationActive={false}
          type='stepBefore'
          dataKey='value'
          stroke='#71A7B2'
          strokeWidth={2}
          fill='url(#waterHoldingCapacity)'
          fillOpacity={1}
        />
        <XAxis
          hide
          dataKey='date'
          scale='time'
          type='number'
          domain={['dataMin', 'dataMax']}
          ticks={xTicks}
          padding={{ left: 8, right: 8 }}
        />
        <YAxis
          hide
          dataKey='value'
          type='number'
          scale='sequential'
          width={0}
          domain={minMaxAxisDomain(0, 1.2)}
          padding={{ top: 8, bottom: 8 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
